import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { assertOk } from '@cotera/utilities';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading } from '@cotera/client/app/components';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Textarea } from '@cotera/client/v0/components/ui/textarea';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { ConnectWarehouseForm } from '@cotera/client/app/components/warehouse/connect-warehouse-form';
import { SchedulePicker } from '../trigger/schedule-picker';

/** Default warehouse re-scan cadence: once a day at 09:00 UTC. */
const DEFAULT_WAREHOUSE_CADENCE = '0 9 * * *';

/**
 * The "add a warehouse dataset" flow, in one modal: connect a warehouse if the
 * org has none, then enter the SELECT to seed from. It mirrors CSV onboarding —
 * pick the type, get set up, then land on the dataset page — with the connect
 * step standing in for the file picker.
 *
 * The connection check is a plain query rather than the suspense hook the rest
 * of the app reads it through, so this renders inside the action modal without
 * needing a Suspense boundary around it; creating a connection invalidates the
 * shared key so the settings view and everything else see it too.
 */
export const WarehouseDatasetSetupView: React.FC<{
  onSubmit: (params: {
    sourceQuery: string;
    rowKeyColumns: string[];
    name?: string;
    cadence: string;
  }) => void;
}> = ({ onSubmit }) => {
  const org = useWhoami((s) => s.org);
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const connectionKey = queryKeys.warehouse.getPrimary({ orgId: org.id });
  const connection = useQuery({
    queryKey: connectionKey,
    queryFn: async () => assertOk(await client.warehouse.getPrimary({})),
    // Always revalidate: this decides whether to prompt for a connection, so it
    // must not trust a cached value that the app root fetched earlier (a
    // connection can have been added or removed since).
    staleTime: 0,
    refetchOnMount: 'always',
  });

  if (connection.isPending) {
    return (
      <ModalPageView title="New warehouse dataset" className="px-4 py-6">
        <div className="flex items-center justify-center py-8">
          <Loading.Spinner />
        </div>
      </ModalPageView>
    );
  }

  if (connection.data === null || connection.data === undefined) {
    return (
      <ModalPageView
        title="Connect a warehouse"
        subtitle="A warehouse connection is needed before adding a warehouse dataset."
        className="px-4 py-4"
      >
        <ConnectWarehouseForm
          onConnected={() =>
            queryClient.invalidateQueries({ queryKey: connectionKey })
          }
        />
      </ModalPageView>
    );
  }

  return (
    <WarehouseSelectForm
      warehouseName={connection.data.displayName}
      onSubmit={onSubmit}
    />
  );
};

/** The SELECT that seeds the dataset, a row key, a re-scan cadence, and a name. */
const WarehouseSelectForm: React.FC<{
  warehouseName: string;
  onSubmit: (params: {
    sourceQuery: string;
    rowKeyColumns: string[];
    name?: string;
    cadence: string;
  }) => void;
}> = ({ warehouseName, onSubmit }) => {
  const [name, setName] = useState('');
  const [sourceQuery, setSourceQuery] = useState('');
  const [rowKeys, setRowKeys] = useState('');
  const [cadence, setCadence] = useState(DEFAULT_WAREHOUSE_CADENCE);

  const rowKeyColumns = rowKeys
    .split(',')
    .map((column) => column.trim())
    .filter((column) => column !== '');
  const canSubmit = sourceQuery.trim() !== '' && rowKeyColumns.length > 0;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    const trimmedName = name.trim();
    onSubmit({
      sourceQuery: sourceQuery.trim(),
      rowKeyColumns,
      cadence,
      ...(trimmedName !== '' && { name: trimmedName }),
    });
  };

  return (
    <ModalPageView
      title="New warehouse dataset"
      subtitle={`Seeded from a query against ${warehouseName}.`}
      className="px-4 py-4"
    >
      <form className="flex flex-col gap-3" onSubmit={submit}>
        <Field label="Name (optional)">
          <Input
            value={name}
            placeholder="Untitled dataset"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="SELECT statement">
          <Textarea
            value={sourceQuery}
            rows={8}
            spellCheck={false}
            className="font-mono text-xs"
            placeholder="select * from analytics.orders"
            onChange={(e) => setSourceQuery(e.target.value)}
          />
        </Field>
        <Text.P className="text-xs text-muted-foreground">
          The preview holds the first 20,000 rows; the full result stays in your
          warehouse.
        </Text.P>
        <Field label="Row key column(s)">
          <Input
            value={rowKeys}
            placeholder="id"
            onChange={(e) => setRowKeys(e.target.value)}
          />
          <span className="text-xs text-muted-foreground">
            Column(s) that uniquely identify a row, comma-separated. Required —
            change detection and preview top-ups match rows by this key, so it
            must be returned by the query.
          </span>
        </Field>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            Change detection schedule
          </span>
          <SchedulePicker value={cadence} onChange={setCadence} />
          <span className="text-xs text-muted-foreground">
            How often the warehouse is re-scanned for changes to this query's
            rows.
          </span>
        </div>
        <div className="flex justify-end pt-1">
          <Button type="submit" disabled={!canSubmit}>
            Create dataset
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    {children}
  </label>
);
